/** Runtime configuration, sourced from per-environment Vite env vars.
 *
 * Never hardcode the backend host or the webext_uuid — both are environment- and
 * install-specific (see README "Identity & auth"). In production the webext_uuid is
 * provisioned per merchant at OAuth onboarding; in dev it comes from `.env`.
 */
/** The path the hosting rewrite proxies to `badgerv2-pos`. Relative on purpose: it
 * resolves against whatever origin the extension was served from, which is the origin
 * the web view holds credentials for. */
export const DEFAULT_POS_SURFACE_PATH = '/api/pos'

export interface AppConfig {
  /** Base URL of the badger-backend `lightspeed_integration` function (V1). */
  backendBaseUrl: string
  /** The V2 POS surface (DEV-378). A DIFFERENT service from `backendBaseUrl`, not a
   * path on it: `badgerv2_pos` speaks the versioned envelope, authenticates a scoped
   * POS credential over HTTP Basic, and answers CORS from an allow-list rather than
   * V1's `*`. Kept separate so the two can be pointed at different hosts during the
   * migration, and so a CORS probe of one cannot be mistaken for the other. */
  posSurfaceUrl: string
  /** Per-install identifier (business + location), V1 only.
   *
   * DEV-378: this no longer authenticates anything. V2 presents a real per-location
   * credential (see `config/credentials.ts`), so `webext_uuid` is kept solely to keep
   * the V1 endpoints callable during the migration and MUST NOT gate startup — it was
   * blocking the app on a value nothing uses any more.
   */
  webextUuid: string
}

export const config: AppConfig = {
  backendBaseUrl: (import.meta.env.VITE_BACKEND_BASE_URL ?? '').replace(/\/$/, ''),
  // Same-origin by default (see the rewrite in firebase.json). Same-origin is what
  // lets the web view replay the Basic credentials K-Series authenticated the page
  // with, and it sidesteps CORS entirely. The env var overrides it for local dev,
  // where there is no hosting rewrite to proxy through.
  posSurfaceUrl:
    (import.meta.env.VITE_POS_SURFACE_URL ?? DEFAULT_POS_SURFACE_PATH).replace(/\/$/, ''),
  webextUuid: import.meta.env.VITE_WEBEXT_UUID ?? '',
}

/** Can this till talk to the V2 points backend at all?
 *
 * A surface to call and a credential to call it with. Deliberately NOT `webextUuid`:
 * that identified a V1 install and proved nothing, and requiring it stopped the app
 * booting for a location that is correctly configured for V2.
 */
export function isConfigured(): boolean {
  // A stored credential is no longer required. When the surface is same-origin the web
  // view attaches the Basic credentials K-Series authenticated the page with, and the
  // client never sees them — which is strictly better than holding a password. An
  // explicitly configured credential still works for the cross-origin case (local dev,
  // or a deployment without the rewrite).
  return Boolean(config.posSurfaceUrl)
}

/** The V1 endpoints still need their identifier while both generations run. */
export function isV1Configured(): boolean {
  return Boolean(config.backendBaseUrl && config.webextUuid)
}
